import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  type Column,
  type ColumnDef,
  getCoreRowModel,
  type OnChangeFn,
  type RowSelectionState,
  getSortedRowModel,
  type SortingState,
  type Table,
  type VisibilityState,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarClock,
  Layers,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { CampaignAnalyticsWarnings } from '@/components/campaign-analytics-warnings';
import { CampaignRowActionsMenu } from '@/components/campaigns/campaign-row-actions-menu';
import { CampaignLabelsIcon } from '@/components/campaign-labels-icon';
import { formatPercentFromRatio } from '@/components/metrics/formatters';
import type { Locales } from '@/i18n/i18n-types';
import { useLocalSettings } from '@/hooks/use-local-settings';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import { cn, formatShortDateTime } from '@/lib/utils';

export type CampaignsTableLabels = {
  title: string;
  launchDate: string;
  audience: string;
  entireUserbase: string;
  revenue: string;
  actions: string;
  scheduled: string;
  draft: string;
  batch: string;
  paidOrderCount: string;
  conversionRate: string;
  impressions: string;
  clicks: string;
  ctr: string;
  openCampaign: string;
};

export type CampaignsTableFooterValues = {
  revenueSum: ReactNode;
  paidOrdersSum: string;
  impressionsSum: string;
  clicksSum: string;
  conversionRateAvg: string;
  ctrAvg: string;
};

/** Anything not yet sent can be deleted, drafts with a past date included. */
export function canDeleteCampaigns(campaigns: CampaignDto[]): boolean {
  return (
    campaigns.length > 0 &&
    campaigns.every((campaign) => campaign.status !== 'sent')
  );
}

function SortableHeader({
  label,
  column,
  align = 'left',
}: {
  label: string;
  column: Column<CampaignDto, unknown>;
  align?: 'left' | 'right';
}) {
  const sortDirection = column.getIsSorted();
  const button = (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(column.getIsSorted() !== 'desc')}
      className={cn(
        'h-8 shrink-0 gap-1 whitespace-nowrap px-2 -mx-2',
        sortDirection && 'font-bold'
      )}
    >
      <span>{label}</span>
      {sortDirection === 'asc' ? (
        <ArrowUp className="h-4 w-4 shrink-0" />
      ) : sortDirection === 'desc' ? (
        <ArrowDown className="h-4 w-4 shrink-0" />
      ) : (
        <ArrowUpDown className="h-4 w-4 shrink-0 opacity-50" />
      )}
    </Button>
  );

  if (align === 'right') {
    return <div className="flex w-full min-w-max justify-end">{button}</div>;
  }

  return button;
}

export function useCampaignsTable(params: {
  campaigns: CampaignDto[];
  analyticsEnabled: boolean;
  audiencesEnabled: boolean;
  tenantSlug: string;
  locale: Locales;
  /** `undefined` while the store currency loads: revenue shows a skeleton. */
  currency: string | undefined;
  labels: CampaignsTableLabels;
  onOpenCampaign: (campaignId: string) => void;
  /** Row ⋯ › Duplicar (hidden without it). */
  onDuplicateCampaign?: (campaign: CampaignDto) => void;
  /** Row ⋯ › Cancelar, for campaigns not sent (hidden without it). */
  onCancelCampaign?: (campaign: CampaignDto) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
}): {
  table: Table<CampaignDto>;
  footerValues: CampaignsTableFooterValues | null;
} {
  const {
    campaigns,
    analyticsEnabled,
    audiencesEnabled,
    tenantSlug,
    locale,
    currency,
    labels,
    onOpenCampaign,
    onDuplicateCampaign,
    onCancelCampaign,
    rowSelection: controlledRowSelection,
    onRowSelectionChange: controlledOnRowSelectionChange,
  } = params;

  const { localSettings, setLocalSettings } = useLocalSettings();
  const localSettingsRef = useRef(localSettings);
  localSettingsRef.current = localSettings;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalRowSelection, setInternalRowSelection] =
    useState<RowSelectionState>({});
  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const onRowSelectionChange =
    controlledOnRowSelectionChange ?? setInternalRowSelection;
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => localSettings.campaign.listTableColumnVisibility
  );

  const currencyFormatter = useMemo(
    () =>
      currency === undefined
        ? null
        : new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
          }),
    [locale, currency]
  );

  const formatRevenue = useCallback(
    (value: number): ReactNode =>
      currencyFormatter === null ? (
        <Skeleton
          data-testid="currency-skeleton"
          className="ml-auto inline-block h-4 w-16 align-middle"
        />
      ) : (
        currencyFormatter.format(value)
      ),
    [currencyFormatter]
  );

  const formatNumber = useCallback(
    (value: number) => new Intl.NumberFormat(locale).format(value),
    [locale]
  );

  const campaignMetaById = useMemo(() => {
    const nowMs = Date.now();
    const meta = new Map<string, { isScheduled: boolean }>();

    for (const campaign of campaigns) {
      const launchMs = Date.parse(campaign.launchDateTime);
      // A draft never went out, whatever its date: it has no metrics to show.
      meta.set(campaign.id, {
        isScheduled:
          campaign.status === 'draft' ||
          (Number.isFinite(launchMs) && launchMs > nowMs),
      });
    }

    return meta;
  }, [campaigns]);

  const isCampaignScheduled = useCallback(
    (campaign: CampaignDto) =>
      campaignMetaById.get(campaign.id)?.isScheduled ?? false,
    [campaignMetaById]
  );

  const columns = useMemo<ColumnDef<CampaignDto>[]>(() => {
    const base: ColumnDef<CampaignDto>[] = [
      {
        id: 'select',
        enableSorting: false,
        enableHiding: false,
        header: ({ table }) => (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={
                table.getIsAllRowsSelected()
                  ? true
                  : table.getIsSomeRowsSelected()
                    ? 'indeterminate'
                    : false
              }
              onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
              aria-label="Select all campaigns"
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div
            className="flex items-center justify-center"
            onClick={(event) => event.stopPropagation()}
          >
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label={`Select campaign ${row.original.name}`}
            />
          </div>
        ),
      },
      {
        id: 'launchDateTime',
        accessorKey: 'launchDateTime',
        enableSorting: true,
        enableHiding: analyticsEnabled,
        meta: { columnLabel: labels.launchDate },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.launchDateTime;
          const b = rowB.original.launchDateTime;
          return a.localeCompare(b);
        },
        header: ({ column }) => (
          <SortableHeader label={labels.launchDate} column={column} />
        ),
        cell: ({ row }) => (
          <span>
            {formatShortDateTime(row.original.launchDateTime, locale)}
          </span>
        ),
      },
    ];

    if (audiencesEnabled) {
      base.push({
        id: 'audience',
        accessorFn: (row) => row.audience?.name ?? '',
        enableSorting: true,
        enableHiding: true,
        meta: { columnLabel: labels.audience },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.audience?.name ?? '';
          const b = rowB.original.audience?.name ?? '';
          return a.localeCompare(b, undefined, { sensitivity: 'base' });
        },
        header: ({ column }) => (
          <SortableHeader label={labels.audience} column={column} />
        ),
        cell: ({ row }) => {
          const audience = row.original.audience;
          if (audience === null) {
            return <span className="truncate">{labels.entireUserbase}</span>;
          }
          return (
            <Link
              to={`/${tenantSlug}/audiences/${audience.id}`}
              className="text-primary underline truncate"
              onClick={(event) => event.stopPropagation()}
            >
              {audience.name}
            </Link>
          );
        },
      });
    }

    base.push({
      id: 'name',
      accessorKey: 'name',
      enableSorting: true,
      enableHiding: false,
      sortingFn: (rowA, rowB) =>
        rowA.original.name.localeCompare(rowB.original.name, undefined, {
          sensitivity: 'base',
        }),
      header: ({ column }) => (
        <SortableHeader label={labels.title} column={column} />
      ),
      cell: ({ row }) => {
        const campaign = row.original;
        const scheduled = isCampaignScheduled(campaign);
        return (
          <div
            className={cn(
              'font-medium flex items-center gap-2 min-w-0 whitespace-nowrap'
            )}
          >
            {scheduled && campaign.status !== 'draft' && (
              <span
                className="inline-flex items-center shrink-0"
                aria-label="Programada"
                title="Programada"
              >
                <CalendarClock className="h-4 w-4" aria-hidden />
              </span>
            )}
            {!scheduled && (
              <CampaignAnalyticsWarnings
                analytics={campaign.analytics}
                closeOnScroll
              />
            )}
            <CampaignLabelsIcon labels={campaign.labels} closeOnScroll />
            <span className="truncate">{campaign.name}</span>
            {campaign.status === 'draft' && (
              <Badge variant="outline" className="shrink-0">
                {labels.draft}
              </Badge>
            )}
            {campaign.importBatchId !== null && (
              <Link
                to={`/${tenantSlug}/campaigns/import/${campaign.importBatchId}`}
                className="inline-flex shrink-0 items-center gap-1 text-xs font-normal text-primary underline"
                onClick={(event) => event.stopPropagation()}
              >
                <Layers className="h-3 w-3" aria-hidden />
                {labels.batch}
              </Link>
            )}
          </div>
        );
      },
    });

    if (analyticsEnabled) {
      base.push(
        {
          id: 'revenue',
          accessorFn: (row) => row.analytics.revenue,
          enableSorting: true,
          enableHiding: true,
          meta: { columnLabel: labels.revenue },
          header: ({ column }) => (
            <SortableHeader
              label={labels.revenue}
              column={column}
              align="right"
            />
          ),
          cell: ({ row }) => {
            const campaign = row.original;
            const scheduled = isCampaignScheduled(campaign);
            return (
              <div className={cn('text-right tabular-nums')}>
                {scheduled ? null : formatRevenue(campaign.analytics.revenue)}
              </div>
            );
          },
        },
        {
          id: 'impressions',
          accessorFn: (row) => row.analytics.notificationsSuccessfullyDelivered,
          enableSorting: true,
          enableHiding: true,
          meta: { columnLabel: labels.impressions },
          header: ({ column }) => (
            <SortableHeader
              label={labels.impressions}
              column={column}
              align="right"
            />
          ),
          cell: ({ row }) => {
            const campaign = row.original;
            const scheduled = isCampaignScheduled(campaign);
            return (
              <div className={cn('text-right tabular-nums')}>
                {scheduled
                  ? null
                  : campaign.analytics.notificationsSuccessfullyDelivered}
              </div>
            );
          },
        },
        {
          id: 'clickCount',
          accessorFn: (row) => row.analytics.clickCount,
          enableSorting: true,
          enableHiding: true,
          meta: { columnLabel: labels.clicks },
          header: ({ column }) => (
            <SortableHeader
              label={labels.clicks}
              column={column}
              align="right"
            />
          ),
          cell: ({ row }) => {
            const campaign = row.original;
            const scheduled = isCampaignScheduled(campaign);
            const shouldHideData =
              campaign.analytics.dataSufficiency.shouldHideData;
            return (
              <div className={cn('text-right tabular-nums')}>
                {scheduled
                  ? null
                  : shouldHideData
                    ? '-'
                    : campaign.analytics.clickCount}
              </div>
            );
          },
        },
        {
          id: 'ctr',
          accessorFn: (row) => row.analytics.ctr,
          enableSorting: true,
          enableHiding: true,
          meta: { columnLabel: labels.ctr },
          header: ({ column }) => (
            <SortableHeader label={labels.ctr} column={column} align="right" />
          ),
          cell: ({ row }) => {
            const campaign = row.original;
            const scheduled = isCampaignScheduled(campaign);
            const shouldHideData =
              campaign.analytics.dataSufficiency.shouldHideData;
            return (
              <div className={cn('text-right tabular-nums')}>
                {scheduled
                  ? null
                  : shouldHideData
                    ? '-'
                    : formatPercentFromRatio(campaign.analytics.ctr, locale)}
              </div>
            );
          },
        },
        {
          id: 'conversionRate',
          accessorFn: (row) => row.analytics.conversionRate,
          enableSorting: true,
          enableHiding: true,
          meta: { columnLabel: labels.conversionRate },
          header: ({ column }) => (
            <SortableHeader
              label={labels.conversionRate}
              column={column}
              align="right"
            />
          ),
          cell: ({ row }) => {
            const campaign = row.original;
            const scheduled = isCampaignScheduled(campaign);
            const shouldHideData =
              campaign.analytics.dataSufficiency.shouldHideData;
            return (
              <div className={cn('text-right tabular-nums')}>
                {scheduled
                  ? null
                  : shouldHideData
                    ? '-'
                    : formatPercentFromRatio(
                        campaign.analytics.conversionRate,
                        locale
                      )}
              </div>
            );
          },
        },
        {
          id: 'paidOrderCount',
          accessorFn: (row) => row.analytics.paidOrderCount,
          enableSorting: true,
          enableHiding: true,
          meta: { columnLabel: labels.paidOrderCount },
          header: ({ column }) => (
            <SortableHeader
              label={labels.paidOrderCount}
              column={column}
              align="right"
            />
          ),
          cell: ({ row }) => {
            const campaign = row.original;
            const scheduled = isCampaignScheduled(campaign);
            return (
              <div className={cn('text-right tabular-nums')}>
                {scheduled ? null : campaign.analytics.paidOrderCount}
              </div>
            );
          },
        }
      );
    }

    base.push({
      id: 'actions',
      header: () => <span className="sr-only">{labels.actions}</span>,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="text-right" onClick={(e) => e.stopPropagation()}>
          <CampaignRowActionsMenu
            campaign={row.original}
            viewDetailsLabel={labels.openCampaign}
            onViewDetails={(campaign) => onOpenCampaign(campaign.id)}
            onDuplicate={onDuplicateCampaign}
            onCancel={onCancelCampaign}
          />
        </div>
      ),
    });

    return base;
  }, [
    analyticsEnabled,
    audiencesEnabled,
    formatRevenue,
    isCampaignScheduled,
    labels,
    locale,
    onCancelCampaign,
    onDuplicateCampaign,
    onOpenCampaign,
    tenantSlug,
  ]);

  const table = useReactTable({
    data: campaigns,
    columns,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
    },
    enableRowSelection: true,
    onSortingChange: setSorting,
    onRowSelectionChange,
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const prevSettings = localSettingsRef.current;
        setLocalSettings({
          ...prevSettings,
          campaign: {
            ...prevSettings.campaign,
            listTableColumnVisibility: next,
          },
        });
        return next;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
  });

  const footerValues = useMemo<CampaignsTableFooterValues | null>(() => {
    if (!analyticsEnabled) {
      return null;
    }

    const selectedRows = table.getSelectedRowModel().rows;
    let selectedCount = 0;
    let visibleMetricsCount = 0;
    const selectedTotals = {
      revenue: 0,
      paidOrders: 0,
      impressions: 0,
      clicks: 0,
      conversionRate: 0,
      ctr: 0,
    };

    for (const row of selectedRows) {
      const campaign = row.original;
      if (isCampaignScheduled(campaign)) {
        continue;
      }

      selectedCount += 1;
      selectedTotals.revenue += campaign.analytics.revenue;
      selectedTotals.paidOrders += campaign.analytics.paidOrderCount;
      selectedTotals.impressions +=
        campaign.analytics.notificationsSuccessfullyDelivered;

      if (!campaign.analytics.dataSufficiency.shouldHideData) {
        visibleMetricsCount += 1;
        selectedTotals.clicks += campaign.analytics.clickCount;
        selectedTotals.conversionRate += campaign.analytics.conversionRate;
        selectedTotals.ctr += campaign.analytics.ctr;
      }
    }

    if (selectedCount === 0) {
      return null;
    }

    const totals = selectedTotals;

    return {
      revenueSum: formatRevenue(totals.revenue),
      paidOrdersSum: formatNumber(totals.paidOrders),
      impressionsSum: formatNumber(totals.impressions),
      clicksSum: visibleMetricsCount > 0 ? formatNumber(totals.clicks) : '-',
      conversionRateAvg:
        visibleMetricsCount > 0
          ? formatPercentFromRatio(
              totals.conversionRate / visibleMetricsCount,
              locale
            )
          : '-',
      ctrAvg:
        visibleMetricsCount > 0
          ? formatPercentFromRatio(totals.ctr / visibleMetricsCount, locale)
          : '-',
    };
    // depend on rowSelection to trigger re-render when it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    analyticsEnabled,
    formatRevenue,
    isCampaignScheduled,
    locale,
    rowSelection,
    table,
    formatNumber,
  ]);

  return { table, footerValues };
}
